import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useToast } from '@shared/hooks/use-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PackagingApiAdapter } from '../adapters/PackagingApiAdapter';
import { PackagingService } from '../services/PackagingService';

interface UseDeletePackagingOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export const useDeletePackaging = (options: UseDeletePackagingOptions = {}) => {
  const queryClient = useQueryClient();
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new PackagingApiAdapter(httpClient);
  const packagingService = new PackagingService(apiAdapter);
  const { toast } = useToast();
  const { t } = useTranslation('materialAlmacenModule');

  const mutation = useMutation({
    mutationFn: (id: number) => packagingService.deletePackaging(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['materialAlmacen'] });
      queryClient.removeQueries({ queryKey: ['materialAlmacen', id] });

      toast({
        title: t('messages.delete_success'),
        description: t('modal.delete.success_description'),
        variant: 'default',
      });

      options.onSuccess?.();
    },
    onError: (error: any) => {
      console.log('🔍 useDeletePackaging - Error:', error);

      // Traducimos códigos de dominio del backend para mensajes claros al usuario.
      const errorCode = error?.response?.data?.error?.error_code || error?.response?.data?.error_code;
      const statusCode = error?.response?.status;
      let errorMessage = error?.message || t('errors.unexpected');

      if (errorCode === 'PACKAGING_NOT_FOUND' || statusCode === 404) {
        errorMessage = t('errors.delete_not_found');
      } else if (errorCode === 'PACKAGING_HAS_STOCK_MOVEMENTS' || statusCode === 409) {
        errorMessage = t('errors.delete_has_stock_movements');
      } else if (statusCode === 422) {
        errorMessage = t('errors.delete_invalid_id');
      }

      toast({
        title: t('messages.delete_error'),
        description: errorMessage,
        variant: 'destructive',
      });

      options.onError?.(error);
    },
  });

  return {
    deletePackaging: mutation.mutate,
    deletePackagingAsync: mutation.mutateAsync,
    isDeleting: mutation.isPending,
    error: mutation.error,
    isSuccess: mutation.isSuccess,
    reset: mutation.reset,
  };
};
